<script lang="ts" module>
  export type NoticeTone = 'info' | 'success' | 'warning' | 'danger' | 'error' | 'brand' | 'neutral';
</script>

<script lang="ts">
  // Inline message inside a page, card, form or dialog.
  //   <Notice tone="warning" title="Let op">Tekst</Notice>
  //   <Notice variant="bar" tone="warning">Je bent offline</Notice>       full-width strip (top of a page/shell)
  //   <Notice variant="dashed" tone="neutral" icon={Move}>Sleep widgets…</Notice>   hint / edit mode
  //   <Notice dismissible ondismiss={…}>…</Notice>
  // Variants: soft (tinted box, default) · bar (strip without radius) · dashed (quiet hint).
  // `kind` is the old name of `tone` ('error' = 'danger') and still works.
  import type { Component, Snippet } from 'svelte';
  import { slide } from 'svelte/transition';
  import { CircleAlert, CircleCheck, Info, TriangleAlert, Sparkles, X } from '@lucide/svelte';
  import '../messages/ui';
  import { t } from '../i18n.svelte';

  interface Props {
    tone?: NoticeTone;
    /** @deprecated use `tone`. */
    kind?: 'info' | 'success' | 'error' | 'warning';
    variant?: 'soft' | 'bar' | 'dashed';
    title?: string;
    /** Custom icon, or false for none. */
    icon?: Component<any> | false;
    size?: 'sm' | 'md';
    dismissible?: boolean;
    ondismiss?: () => void;
    /** ARIA role; defaults to alert for danger, status otherwise. */
    role?: 'alert' | 'status' | 'note' | 'none';
    class?: string;
    children?: Snippet;
    actions?: Snippet;
  }
  let { tone, kind, variant = 'soft', title, icon, size = 'md', dismissible = false, ondismiss, role, class: cls = '', children, actions }: Props = $props();

  let dismissed = $state(false);
  const resolved = $derived<Exclude<NoticeTone, 'error'>>(((tone ?? kind ?? 'info') === 'error' ? 'danger' : (tone ?? kind ?? 'info')) as Exclude<NoticeTone, 'error'>);
  const DEFAULT_ICONS = { info: Info, success: CircleCheck, warning: TriangleAlert, danger: CircleAlert, brand: Sparkles, neutral: Info };
  const Icon = $derived(icon === false ? null : (icon ?? DEFAULT_ICONS[resolved]));
  const ariaRole = $derived(role === 'none' ? undefined : (role ?? (resolved === 'danger' ? 'alert' : 'status')));

  function dismiss() {
    dismissed = true;
    ondismiss?.();
  }
</script>

{#if !dismissed}
  <div class="notice {resolved} {variant} {size} {cls}" role={ariaRole} transition:slide={{ duration: 160 }}>
    {#if Icon}<span class="icon" aria-hidden="true"><Icon /></span>{/if}
    <div class="content">
      {#if title}<strong class="title">{title}</strong>{/if}
      {#if children}<div class="text">{@render children()}</div>{/if}
      {#if actions}<div class="actions">{@render actions()}</div>{/if}
    </div>
    {#if dismissible}
      <button type="button" class="x" aria-label={t('ui.dismiss')} onclick={dismiss}><X /></button>
    {/if}
  </div>
{/if}

<style>
  .notice {
    --tone: var(--info);
    --tone-soft: var(--info-soft);
    display: flex;
    gap: var(--space-2_5);
    align-items: flex-start;
    padding: var(--space-3) var(--space-3);
    border-radius: var(--radius);
    border: 1px solid color-mix(in srgb, var(--tone) 22%, transparent);
    background: var(--tone-soft);
    font-size: var(--text-sm);
    line-height: var(--leading-normal);
    min-width: 0;
  }
  .sm { padding: var(--space-2) var(--space-2_5); font-size: var(--text-xs); gap: var(--space-2); }
  .info { --tone: var(--info); --tone-soft: var(--info-soft); }
  .success { --tone: var(--success); --tone-soft: var(--success-soft); }
  .warning { --tone: var(--warning); --tone-soft: var(--warning-soft); }
  .danger { --tone: var(--danger); --tone-soft: var(--danger-soft); }
  .brand { --tone: var(--brand); --tone-soft: var(--brand-softer); }
  .neutral { --tone: var(--text-3); --tone-soft: var(--surface-2); }
  .neutral { border-color: var(--border); }

  .bar { border-radius: 0; border-width: 0 0 1px; padding-inline: var(--space-4); align-items: center; }
  .dashed { background: transparent; border-style: dashed; border-color: color-mix(in srgb, var(--tone) 45%, var(--border)); }
  .dashed.neutral { border-color: var(--border-strong); }

  .icon { flex-shrink: 0; display: inline-flex; color: var(--tone); margin-top: 1px; }
  .icon :global(svg) { width: var(--icon-lg); height: var(--icon-lg); }
  .sm .icon :global(svg) { width: var(--icon-md); height: var(--icon-md); }
  .bar .icon { margin-top: 0; }
  .content { flex: 1; display: flex; flex-direction: column; gap: var(--space-0_5); min-width: 0; color: var(--text); }
  .title { font-weight: var(--weight-semibold); }
  .text { color: var(--text-2); }
  .content > .text:first-child { color: var(--text); }
  .actions { display: flex; flex-wrap: wrap; gap: var(--space-2); margin-top: var(--space-1_5); }
  .x {
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    margin: calc(var(--space-0_5) * -1) calc(var(--space-1) * -1) 0 0;
    display: grid;
    place-items: center;
    border: 0;
    border-radius: var(--radius-xs);
    background: transparent;
    color: var(--text-3);
  }
  .x :global(svg) { width: var(--icon-sm); height: var(--icon-sm); }
  .x:hover { background: color-mix(in srgb, var(--tone) 12%, transparent); color: var(--text); }
  .x:focus-visible { outline: none; box-shadow: var(--ring); }
</style>
